import React, { useState, useRef } from "react";
import { toast } from "react-toastify";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { makeStyles } from "@mui/styles";
import { alpha, useTheme } from "@mui/material/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dropZone: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: 8,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    color: "#8A9096",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
  },
  mapRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  result: {
    textAlign: "center",
    padding: theme.spacing(3),
  },
  preview: {
    maxHeight: 200,
    overflowY: "auto",
    marginTop: theme.spacing(1),
  },
}));

const parseCSV = (text) => {
  const sep = text.includes(";") && !text.includes(",") ? ";" : ",";
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map((line) =>
    line.split(sep).reduce((obj, val, i) => {
      obj[headers[i]] = val.trim().replace(/^"|"$/g, "");
      return obj;
    }, {})
  );
  return { headers, rows };
};

const STEP = { UPLOAD: 0, MAP: 1, RESULT: 2 };

const ImportContactsCSVModal = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fileRef = useRef();

  const [step, setStep] = useState(STEP.UPLOAD);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapName, setMapName] = useState("");
  const [mapNumber, setMapNumber] = useState("");
  const [mapEmail, setMapEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(STEP.UPLOAD);
    setHeaders([]);
    setRows([]);
    setMapName("");
    setMapNumber("");
    setMapEmail("");
    setResult(null);
    setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { headers: h, rows: r } = parseCSV(e.target.result);
        setHeaders(h);
        setRows(r);
        // auto-detect common column names
        const nameCol = h.find((c) => /name|nome/i.test(c)) || "";
        const numCol = h.find((c) => /number|numero|whatsapp|telefone|phone/i.test(c)) || "";
        const emailCol = h.find((c) => /email|e-mail/i.test(c)) || "";
        setMapName(nameCol);
        setMapNumber(numCol);
        setMapEmail(emailCol);
        setStep(STEP.MAP);
      } catch {
        toast.error("Erro ao processar o CSV. Verifique o formato do arquivo.");
      }
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleImport = async () => {
    if (!mapName || !mapNumber) {
      toast.error("Selecione as colunas de Nome e Número.");
      return;
    }
    setLoading(true);
    try {
      const contacts = rows
        .filter((r) => r[mapNumber])
        .map((r) => ({ name: r[mapName] || r[mapNumber], number: r[mapNumber], email: mapEmail ? r[mapEmail] : "" }));

      const { data } = await api.post("/contacts/import-csv", { contacts });
      setResult(data);
      setStep(STEP.RESULT);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>Importar Contatos via CSV</DialogTitle>
      <DialogContent>
        {step === STEP.UPLOAD && (
          <div
            className={classes.dropZone}
            onClick={() => fileRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Typography variant="body1" style={{ fontWeight: 600, color: theme.palette.primary.main }}>
              Clique ou arraste um arquivo CSV aqui
            </Typography>
            <Typography variant="caption">
              O arquivo deve ter colunas de nome e número de telefone (com DDD)
            </Typography>
          </div>
        )}

        {step === STEP.MAP && (
          <>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              {rows.length} linhas encontradas. Mapeie as colunas:
            </Typography>
            <div className={classes.mapRow}>
              <Typography style={{ minWidth: 60 }}>Nome *</Typography>
              <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
                <InputLabel>Coluna</InputLabel>
                <Select value={mapName} onChange={(e) => setMapName(e.target.value)} label="Coluna">
                  <MenuItem value="">—</MenuItem>
                  {headers.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </Select>
              </FormControl>
            </div>
            <div className={classes.mapRow}>
              <Typography style={{ minWidth: 60 }}>Número *</Typography>
              <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
                <InputLabel>Coluna</InputLabel>
                <Select value={mapNumber} onChange={(e) => setMapNumber(e.target.value)} label="Coluna">
                  <MenuItem value="">—</MenuItem>
                  {headers.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </Select>
              </FormControl>
            </div>
            <div className={classes.mapRow}>
              <Typography style={{ minWidth: 60 }}>Email</Typography>
              <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
                <InputLabel>Coluna</InputLabel>
                <Select value={mapEmail} onChange={(e) => setMapEmail(e.target.value)} label="Coluna">
                  <MenuItem value="">—</MenuItem>
                  {headers.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </Select>
              </FormControl>
            </div>

            <Typography variant="caption" style={{ color: "#8A9096" }}>
              Prévia das primeiras 5 linhas:
            </Typography>
            <div className={classes.preview}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headers.map((h) => <TableCell key={h} style={{ fontSize: 11 }}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(0, 5).map((row, i) => (
                    <TableRow key={i}>
                      {headers.map((h) => <TableCell key={h} style={{ fontSize: 11 }}>{row[h]}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {step === STEP.RESULT && result && (
          <div className={classes.result}>
            <CheckCircleIcon style={{ fontSize: 48, color: "#27AE60" }} />
            <Typography variant="h6" style={{ marginTop: 8 }}>Importação concluída!</Typography>
            <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 12 }}>
              <Chip label={`${result.created} criados`} style={{ backgroundColor: "#27AE60", color: "#fff" }} />
              <Chip label={`${result.skipped} ignorados (já existem)`} style={{ backgroundColor: "#8A9096", color: "#fff" }} />
            </div>
            {result.errors?.length > 0 && (
              <Typography variant="caption" style={{ color: "#E74C3C", marginTop: 8, display: "block" }}>
                <ErrorIcon style={{ fontSize: 14, verticalAlign: "middle" }} />
                {" "}{result.errors.length} erro(s) ao processar algumas linhas.
              </Typography>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {step === STEP.UPLOAD && <Button onClick={handleClose}>Cancelar</Button>}
        {step === STEP.MAP && (
          <>
            <Button onClick={reset}>Voltar</Button>
            <Button onClick={handleImport} color="primary" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : `Importar ${rows.length} contatos`}
            </Button>
          </>
        )}
        {step === STEP.RESULT && (
          <Button onClick={handleClose} color="primary" variant="contained">Fechar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportContactsCSVModal;
